const DEFAULT_SW_URL = './sw.js';
const DEFAULT_MANIFEST_URL = './release-manifest.json';

/**
 * Register the current service worker and reload once when an already
 * controlled page receives a replacement controller.
 *
 * Every browser dependency is injected so the update policy can be tested
 * without navigator, location or a network. The caller owns presentation of
 * diagnostic events; this module never sends telemetry.
 */
export async function setupPwaUpdate({
  serviceWorker,
  reload,
  fetchFn = null,
  expectedBuildId = null,
  swUrl = DEFAULT_SW_URL,
  manifestUrl = DEFAULT_MANIFEST_URL,
  onDiagnostic = () => {},
} = {}) {
  const report = createReporter(onDiagnostic);
  const supported = Boolean(
    serviceWorker
      && typeof serviceWorker.register === 'function'
      && typeof serviceWorker.addEventListener === 'function',
  );

  if (!supported) {
    report('pwa_unsupported');
    return {
      supported: false,
      hadControllerAtStart: false,
      registration: null,
      updateRequested: false,
      manifest: await checkReleaseManifestBuild({
        fetchFn,
        expectedBuildId,
        manifestUrl,
        onDiagnostic: report,
      }),
      dispose() {},
    };
  }
  if (typeof reload !== 'function') throw new TypeError('reload doit être une fonction');

  const hadControllerAtStart = Boolean(serviceWorker.controller);
  let reloadHandled = false;
  let listening = true;
  const handleControllerChange = () => {
    if (!hadControllerAtStart) {
      report('pwa_controller_changed', {
        reload: false,
        reason: 'first-install',
      });
      return;
    }
    if (reloadHandled) {
      report('pwa_controller_changed', {
        reload: false,
        reason: 'already-handled',
      });
      return;
    }

    // Set the guard before calling the injected function: even a throwing or
    // re-entrant reload implementation must never be invoked a second time.
    reloadHandled = true;
    report('pwa_controller_changed', { reload: true, reason: 'updated-controller' });
    try {
      reload();
    } catch (error) {
      report('pwa_reload_failed', errorDetails(error));
    }
  };
  serviceWorker.addEventListener('controllerchange', handleControllerChange);

  const dispose = () => {
    if (!listening) return;
    listening = false;
    serviceWorker.removeEventListener?.('controllerchange', handleControllerChange);
  };

  let registration = null;
  let updateRequested = false;
  try {
    registration = await serviceWorker.register(swUrl, { updateViaCache: 'none' });
    report('pwa_registered', { swUrl, hadControllerAtStart });
    if (typeof registration?.update !== 'function') {
      throw new TypeError('ServiceWorkerRegistration.update indisponible');
    }
    updateRequested = true;
    report('pwa_update_requested', { swUrl });
    await registration.update();
  } catch (error) {
    report(registration ? 'pwa_update_failed' : 'pwa_registration_failed', {
      swUrl,
      ...errorDetails(error),
    });
  }

  const manifest = await checkReleaseManifestBuild({
    fetchFn,
    expectedBuildId,
    manifestUrl,
    onDiagnostic: report,
  });

  return {
    supported: true,
    hadControllerAtStart,
    registration,
    updateRequested,
    manifest,
    dispose,
  };
}

/**
 * Optionally compare the running HTML build id with the immutable release
 * manifest. A failed or mismatched check is diagnostic only and never blocks
 * service-worker registration.
 */
export async function checkReleaseManifestBuild({
  fetchFn,
  expectedBuildId,
  manifestUrl = DEFAULT_MANIFEST_URL,
  onDiagnostic = () => {},
} = {}) {
  const report = createReporter(onDiagnostic);
  if (typeof fetchFn !== 'function' || typeof expectedBuildId !== 'string' || expectedBuildId.length === 0) {
    return { checked: false, matches: null, expectedBuildId, actualBuildId: null };
  }

  try {
    const response = await fetchFn(manifestUrl, { cache: 'no-store' });
    if (!response?.ok) {
      throw new Error(`release-manifest HTTP ${response?.status ?? 'inconnu'}`);
    }
    const manifest = await response.json();
    if (typeof manifest?.buildId !== 'string' || manifest.buildId.length === 0) {
      throw new TypeError('release-manifest buildId invalide');
    }

    const actualBuildId = manifest.buildId;
    const matches = actualBuildId === expectedBuildId;
    report(matches ? 'pwa_manifest_match' : 'pwa_manifest_mismatch', {
      expectedBuildId,
      actualBuildId,
    });
    return { checked: true, matches, expectedBuildId, actualBuildId };
  } catch (error) {
    report('pwa_manifest_check_failed', {
      manifestUrl,
      expectedBuildId,
      ...errorDetails(error),
    });
    return {
      checked: false,
      matches: null,
      expectedBuildId,
      actualBuildId: null,
      error: errorDetails(error),
    };
  }
}

function createReporter(onDiagnostic) {
  if (typeof onDiagnostic !== 'function') return () => {};
  return (kind, details = {}) => {
    try {
      const result = onDiagnostic(kind, details);
      if (result && typeof result.then === 'function') {
        Promise.resolve(result).catch(() => {});
      }
    } catch {
      // Diagnostics must never break the update path.
    }
  };
}

function errorDetails(error) {
  return {
    name: error instanceof Error ? error.name : 'Error',
    message: error instanceof Error ? error.message : String(error),
  };
}
